
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import './CustomerProfile.css';

interface Customer {
    _id: string;
    name: string;
    phone: string;
    email?: string;
    status: 'new' | 'returning' | 'vip';
    firstVisit?: string;
    lastVisit?: string;
    totalBookings: number;
}

const CustomerProfile = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (id) fetchCustomer();
    }, [id]);

    const fetchCustomer = async () => {
        try {
            // Assuming we have getCustomerById endpoint (we created it in backend)
            // But api.ts needs a specific method or we reuse getCustomers with filter?
            // Wait, we created valid backend route: GET /api/customers/:id
            // But we didn't add getCustomerById to frontend api.ts yet, only getCustomers with params.
            // Let's use direct redundant fetch or update api.ts. 
            // Updating api.ts is better but for speed I might just use the generic fetch in component for now 
            // or better, update api.ts quickly or assume getCustomers can handle ID? 
            // The getCustomers in api.ts takes params and does `?query`. 
            // Let's quickly update api.ts to include getCustomerById method or just use raw fetch here if I want to avoid context switch.
            // Actually, I'll add `getCustomerById` to `api.ts` in the next tool call.
            // For now, I'll code this assuming `apiService.getCustomerById(id)` exists.

            // Correction: I'll use a direct fetch here to avoid switching files again, or assume I'll fix api.ts
            // Let's invoke a direct fetch helper or just add it to api.ts properly.
            // I will add it to api.ts in the next step.
            const response = await apiService.getCustomerById(id!);
            if (response.success) {
                setCustomer(response.data);
            }
        } catch (error) {
            console.error("Failed to fetch customer", error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div>{t('customers.loading')}</div>;
    if (!customer) return <div>{t('customers.notFound')}</div>;

    const locale = language === 'vi' ? 'vi-VN' : 'en-US';

    return (
        <div className="customer-profile-container">
            <button className="back-btn" onClick={() => navigate('/customers')}>{t('customers.backToList')}</button>

            <div className="profile-header">
                <div className="profile-avatar">
                    {customer.name.charAt(0).toUpperCase()}
                </div>
                <div className="profile-info">
                    <h2>{customer.name}</h2>
                    <span className={`status-badge ${customer.status}`}>{customer.status}</span>
                </div>
            </div>

            <div className="profile-details-grid">
                <div className="detail-card">
                    <h3>{t('customers.contactInfo')}</h3>
                    <p><strong>{t('profile.labels.phone')}:</strong> {customer.phone}</p>
                    <p><strong>{t('profile.labels.fullName').replace('Họ và ', '').replace('Full ', '')}Email:</strong> {customer.email || t('common.na')}</p>
                </div>

                <div className="detail-card">
                    <h3>{t('customers.stats')}</h3>
                    <p><strong>{t('customers.totalBookings')}:</strong> {customer.totalBookings}</p>
                    <p><strong>{t('customers.firstVisit')}:</strong> {customer.firstVisit ? new Date(customer.firstVisit).toLocaleDateString(locale) : t('common.na')}</p>
                    <p><strong>{t('customers.lastVisit2')}:</strong> {customer.lastVisit ? new Date(customer.lastVisit).toLocaleDateString(locale) : t('common.na')}</p>
                </div>
            </div>

            <div className="booking-history-section">
                <h3>{t('customers.recentBookings')}</h3>
                <p className="placeholder-text">{t('customers.bookingHistoryPlaceholder')}</p>
            </div>
        </div>
    );
};

export default CustomerProfile;
